import Footer from "../Footer";
import Navbar from "../Navbar";
import SEO from "../SEO";

const KYCAmlPolicy: React.FC = () => {
  const policySections = [
    {
      title: `KYC & AML Policy for ${import.meta.env.VITE_COMPANY_NAME}`,
      content: `${import.meta.env.VITE_COMPANY_NBFC} is an RBI-registered NBFC specializing in short-term personal loans.`,
    },
    {
      title: "1. Objective",
      content:
        "To prevent misuse of the NBFC’s services for money laundering, terrorist financing, and other unlawful activities by implementing robust Know Your Customer (KYC) and Anti-Money Laundering (AML) procedures.",
    },
    {
      title: "2. Regulatory Framework",
      list: [
        "RBI Master Direction – KYC, 2016 (updated as on August 14, 2025).",
        "Prevention of Money Laundering Act (PMLA), 2002.",
        "RBI’s AML/CFT guidelines and all subsequent circulars and amendments.",
      ],
    },
    {
      title: "3. Customer Acceptance Policy (CAP)",
      list: [
        "No account shall be opened anonymously or under fictitious/benami names.",
        "Mandatory collection of valid identity and address proof before loan disbursal.",
        "Enhanced due diligence for high-risk customers such as PEPs or NRIs.",
        "Accounts shall not be opened if the customer refuses to provide required documentation.",
      ],
    },
    {
      title: "4. Customer Identification Procedure (CIP)",
      list: [
        "Verification through valid KYC documents such as Aadhaar, PAN, Passport, or Voter ID.",
        "Biometric authentication via UIDAI where applicable.",
        "Video KYC permitted for onboarding, subject to regulatory compliance.",
        "Periodic re-KYC for existing customers based on their risk categorization.",
      ],
    },
    {
      title: "5. Risk Categorization",
      list: [
        "Low Risk: Salaried individuals and government employees.",
        "Medium Risk: Self-employed and small business owners.",
        "High Risk: Politically Exposed Persons (PEPs), cash-intensive businesses.",
        "Risk-based KYC updating frequency will apply accordingly.",
      ],
    },
    {
      title: "6. Monitoring of Transactions",
      list: [
        "Automated alert systems will identify suspicious activities such as frequent cash deposits or round-tripping.",
        "Threshold-based reports will be generated for FIU-IND.",
        "Real-time flagging of high-value or unusual transactions.",
      ],
    },
    {
      title: "7. Record Keeping",
      list: [
        "Maintain KYC records for at least 5 years post account closure.",
        "Transaction records retained for 5 years from the transaction date.",
        "All data will be securely stored and made available for regulatory inspection when required.",
      ],
    },
    {
      title: "8. Reporting to Authorities",
      list: [
        "Suspicious Transaction Reports (STRs) will be filed with FIU-IND within prescribed timelines.",
        "Cash Transaction Reports (CTRs) will be filed for cash transactions over ₹10 lakh.",
        "Compliance with RBI’s AML/CFT periodic reporting formats will be ensured.",
      ],
    },
    {
      title: "9. Employee Training & Accountability",
      list: [
        "Ongoing employee training on KYC/AML procedures, red flags, and escalation processes.",
        "Standard operating procedures for onboarding, documentation, and verification.",
        "Defined accountability and escalation matrix for credit and operations teams.",
      ],
    },
    {
      title: "10. Internal Audit & Review",
      list: [
        "Quarterly internal audits focused on KYC/AML compliance effectiveness.",
        "Annual policy review by senior management and the Board.",
        "Timely corrective measures for any lapses or identified deficiencies.",
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-white text-text-primary font-mono selection:bg-secondary selection:text-white ">
      <SEO
        title="KYC & AML Policy"
        description={`Institutional KYC and AML protocols for ${import.meta.env.VITE_COMPANY_NAME}. Compliance with RBI Master Directions and PMLA frameworks for secure credit operations.`}
        keywords={`KYC policy, AML policy, money laundering prevention, ${import.meta.env.VITE_COMPANY_NAME} compliance`}
      />
      <Navbar />

      {/* Official Registry Hero */}
      <section className="w-full bg-text-primary py-32 md:py-48 text-center px-6 relative overflow-hidden border-b-8 border-secondary mb-24">
        <div className="absolute inset-0 opacity-[0.05] pointer-events-none"
          style={{ backgroundImage: `linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)`, backgroundSize: '40px 40px' }} />

        <div className="relative z-10 max-w-5xl mx-auto">
          <div className="inline-flex items-center gap-4 px-6 py-2 bg-white/5 border border-white/10 text-secondary text-[10px] font-black uppercase tracking-[0.5em] mb-12">
            <div className="w-2 h-2 bg-secondary animate-pulse" />
            Compliance_Archive_Protocol_v9.1
          </div>
          <h1 className="text-5xl md:text-[6rem] font-black tracking-tighter leading-[0.85] mb-12 uppercase text-white italic">
            KYC & <span className="text-secondary not-italic">AML Policy.</span>
          </h1>
          <p className="text-white/60 text-lg md:text-2xl max-w-3xl mx-auto font-bold leading-relaxed border-y border-white/10 py-10 uppercase tracking-tight">
            Institutional framework for financial integrity. <br />
            <span className="text-white">Strict adherence to RBI Master Directions for KYC compliance.</span>
          </p>
        </div>
      </section>

      <div className="relative z-10 max-w-5xl mx-auto px-6 lg:px-12 pb-40">
        <div className="border-t-4 border-text-primary">
          {policySections.map((section, idx) => (
            <div key={idx} className="border-b border-text-primary/10 py-16 group">
              <div className="flex flex-col gap-10">
                <div className="flex items-start gap-8">
                  <span className="text-secondary font-black text-xs pt-1">SEC_00{idx + 1}</span>
                  <h2 className="text-3xl md:text-5xl font-black text-text-primary leading-tight uppercase tracking-tighter italic">
                    {section.title.split('. ')[1] || section.title}
                  </h2>
                </div>

                <div className="pl-16 md:pl-24 space-y-8">
                  {section.content && (
                    <p className="text-text-secondary font-bold text-lg leading-relaxed uppercase tracking-tight border-l-4 border-text-primary/5 pl-8 italic">
                      {section.content}
                    </p>
                  )}
                  {section.list && (
                    <ul className="space-y-6">
                      {section.list.map((li, i) => (
                        <li key={i} className="flex items-start gap-4 text-text-primary font-bold text-base leading-relaxed">
                          <div className="w-2 h-2 bg-secondary mt-2 shrink-0" />
                          <span className="uppercase tracking-tight">{li}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default KYCAmlPolicy;
